/**
 * Centralized route constants for the application
 * This file contains all route paths and names used throughout the application
 */

// Auth routes
export const AUTH_ROUTES = {
  LOGIN: {
    NAME: 'Login',
    PATH: '/login',
  },
  SIGNUP_LP: {
    NAME: 'Signup LP',
    PATH: '/signup-lp',
  },
  FORGOT_PASSWORD: {
    NAME: 'Forgot Password',
    PATH: '/forgot-password',
  },
  SIGNUP_BAU: {
    NAME: 'Signup BAU',
    PATH: '/signup-bau',
  },
  SIGNUP_UNAVAILABLE: {
    NAME: 'Signup Unavailable',
    PATH: '/signup-unavailable',
  },
  CHANNEL_LP: {
    NAME: 'Channel LP',
    PATH: '/lp/:slug',
  },
  CHANNEL_BAU: {
    NAME: 'Channel BAU',
    PATH: '/bau/:slug',
  },
  GIFT_INVITE: {
    NAME: 'Gift Invite',
    PATH: '/gift/invite',
  },
  NO_DEVICE: {
    NAME: 'No Device',
    PATH: '/no-device',
  },
} as const;

// Dashboard routes
export const DASHBOARD_ROUTES = {
  ROOT: {
    NAME: 'Root',
    PATH: '/',
  },
  HOME: {
    NAME: 'Home',
    PATH: '/home',
  },
  CHAPTERS: {
    NAME: 'Chapters',
    PATH: '/chapters/:userChapterId',
  },
  WINS: {
    NAME: 'Wins',
    PATH: '/wins',
  },
  PUZZLES_DASHBOARD: {
    NAME: 'Puzzles Dashboard',
    PATH: '/puzzles-dashboard',
  },
} as const;

// Progress routes
export const PROGRESS_ROUTES = {
  ROOT: {
    NAME: 'Progress Routes Wrapper',
    PATH: '/progress-timeline',
  },
  DAILY: {
    NAME: 'Daily Tab',
    PATH: '/progress-timeline/daily',
    RELATIVE_PATH: 'daily',
  },
  MONTHLY: {
    NAME: 'Monthly Tab',
    PATH: '/progress-timeline/monthly',
    RELATIVE_PATH: 'monthly',
  },
} as const;

// User routes
export const USER_ROUTES = {
  PROFILE: {
    NAME: 'Profile',
    PATH: '/profile',
  },
  ACCOUNT: {
    NAME: 'Account',
    PATH: '/account',
  },
} as const;

// Worksheet routes
export const WORKSHEET_ROUTES = {
  RESUME: {
    NAME: 'Worksheet Resume',
    PATH: '/worksheets/:nodeId/resume',
  },
  REVIEW: {
    NAME: 'Worksheet Review',
    PATH: '/worksheets/:studentNodeId/review',
  },
  PREVIEW: {
    NAME: 'Worksheet Preview',
    PATH: '/worksheets/preview',
  },
  DYNAMIC_PREVIEW: {
    NAME: 'Dynamic Worksheet Preview',
    PATH: '/worksheets/dynamic/preview',
  },
  SAT_MOCK_TEST: {
    NAME: 'SAT Mock Test',
    PATH: '/sat-mock-test/:blockId/resume',
  },
  MATHFIT_OVERVIEW: {
    NAME: 'MathFit Assessment',
    PATH: '/mathfit/:testId/overview',
  },
} as const;

// MathQuiz routes
export const MATHQUIZ_ROUTES = {
  PROGRESS: {
    NAME: 'Math Quiz Progress',
    PATH: '/mathquiz',
  },
  REPORT: {
    NAME: 'Math Quiz Report',
    PATH: '/math-quiz/report',
  },
} as const;

// Circle routes
export const CIRCLE_ROUTES = {
  ROOT: {
    NAME: 'Circle Routes Wrapper',
    PATH: '/circle',
  },
  HOME: {
    NAME: 'Circle Home',
    PATH: '/circle',
    RELATIVE_PATH: '',
    SCREEN_NAME: 'home',
  },
  GAME: {
    NAME: 'Circle Game',
    PATH: '/circle/game',
    RELATIVE_PATH: 'game',
    SCREEN_NAME: 'game',
  },
  WORKOUT: {
    NAME: 'Mathgym Workout',
    PATH: '/circle/workout',
    RELATIVE_PATH: 'workout',
    SCREEN_NAME: 'workout',
  },
  LEADERBOARD: {
    NAME: 'Circle Leaderboard',
    PATH: '/circle/leaderboard/:leaderboardType?',
    RELATIVE_PATH: 'leaderboard/:leaderboardType?',
    BASE_RELATIVE_PATH: 'leaderboard',
    SCREEN_NAME: 'leaderboard',
  },
  DOWNLOAD_APP: {
    NAME: 'Mathgym Download App',
    PATH: '/circle/download-app',
    RELATIVE_PATH: 'download-app',
  },
  BACK_TO_FREE_PLAN: {
    NAME: 'Circle Back To Free Plan',
    PATH: '/circle/back-to-free-plan',
    RELATIVE_PATH: 'back-to-free-plan',
  },
} as const;

// Parent routes
export const PARENT_ROUTES = {
  ROOT: {
    NAME: 'Parent Routes Wrapper',
    PATH: '/parent',
  },
  HOME: {
    NAME: 'Parent Home',
    PATH: '/parent/home',
    RELATIVE_PATH: '',
  },
  ONBOARDING: {
    NAME: 'Parent Onboarding',
    PATH: '/parent/onboarding',
    RELATIVE_PATH: 'onboarding',
  },
  BOOK_SLOT: {
    NAME: 'Book Slot',
    PATH: '/parent/onboarding/book-slot',
    RELATIVE_PATH: 'book-slot',
  },
  RESCHEDULE_DEMO: {
    NAME: 'Reschedule Demo',
    PATH: '/parent/onboarding/reschedule-demo',
    RELATIVE_PATH: 'reschedule-demo',
  },
  TUTOR_CHANGE: {
    NAME: 'Tutor Change',
    PATH: '/parent/onboarding/book-slot/tutor-change',
    RELATIVE_PATH: 'book-slot/tutor-change',
  },
  MANAGE_SUBSCRIPTION: {
    NAME: 'Manage Subscription',
    PATH: '/parent/manage-subscription/:studentCourseId',
    RELATIVE_PATH: 'manage-subscription/:studentCourseId',
  },
  RENEWAL: {
    NAME: 'Parent Renewal',
    PATH: '/parent/renewal/:studentCourseId',
    RELATIVE_PATH: 'renewal/:studentCourseId',
  },
} as const;

// Public routes
export const PUBLIC_ROUTES = {
  REMINDER_RESPONSE: {
    NAME: 'Reminder Response',
    PATH: '/reminder-response/:classroomId',
  },
  MONTHLY_PROGRESS_REPORT: {
    NAME: 'Mpr',
    PATH: '/mpr/:studentId',
  },
  PROGRESS_REPORT: {
    NAME: 'Timeline',
    PATH: '/progress-report/:studentId',
  },
  PARENT_FEEDBACK: {
    NAME: 'Parent Feedback',
    PATH: '/parent-feedback/:classroomId',
  },
  PUZZLE_APP_PREVIEW: {
    NAME: 'Puzzle app preview',
    PATH: '/puzzle-app-preview',
  },
  MATHQUIZ_REPORT: {
    NAME: 'MathQuiz Report Public',
    PATH: '/mathquiz/report/:studentId',
  },
} as const;

// Report routes
export const REPORT_ROUTES = {
  DEMO_REPORT: {
    NAME: 'Demo Report',
    PATH: '/demo-report/:studentClassroomId',
  },
  MONTHLY_REPORT: {
    NAME: 'Monthly Report',
    PATH: '/monthly-report/:reportId',
    RELATIVE_PATH: 'monthly-report/:reportId',
  },
  PTM_REPORT: {
    NAME: 'PTM Report',
    PATH: '/ptm-report/:reportId',
    RELATIVE_PATH: 'ptm-report/:reportId',
  },
  PTM_REFERRAL: {
    NAME: 'PTM Referral',
    PATH: '/ptm-referral',
    RELATIVE_PATH: 'ptm-referral',
  },
  MATHFIT_REPORT: {
    NAME: 'MathFit Report',
    PATH: '/mathfit/:testId/report',
  },
  MATHFIT_REPORT_OLD: {
    NAME: 'MathFit Report',
    PATH: '/mathfit/:testId',
  },
} as const;

// Special routes
export const SPECIAL_ROUTES = {
  AUTO_LOGIN: {
    NAME: 'Auto Login',
    PATH: '/auto-login',
  },
  NOT_FOUND: {
    NAME: '404 - Page Not Found',
    PATH: '*',
  },
} as const;

export const PUBLIC_AND_PRIVATE_ROUTES = {
  ACCOUNT_SELECTOR: {
    NAME: 'Account Selector',
    PATH: '/select-account',
  },
  STUDENT_SETUP: {
    NAME: 'Student Setup',
    PATH: '/student-setup',
  },
};
